import { createClient } from "@supabase/supabase-js";
import type { Database } from './types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase environment variables");
}

// Validate that we're not using insecure URLs in production
if (import.meta.env.PROD && (supabaseUrl.startsWith('http://') || supabaseUrl.includes('localhost') || supabaseUrl.includes('127.0.0.1'))) {
  console.error('Insecure Supabase URL detected in production:', supabaseUrl);
  throw new Error("Insecure Supabase URL detected in production environment");
}

// Create a custom storage implementation that handles errors gracefully
const storage = {
  getItem: (key: string): string | null => {
    try {
      const value = localStorage.getItem(key);
      console.log(`Supabase storage getItem(${key}):`, value ? 'found' : 'null');
      
      // Temporarily disable aggressive validation - just log what we find
      if (value && key.includes('auth-token')) {
        try {
          const parsed = JSON.parse(value);
          console.log(`Session data structure for key ${key}:`, Object.keys(parsed));
          // Only remove if completely invalid JSON, not based on structure
        } catch (e) {
          console.warn(`Corrupted session data in localStorage for key ${key}`, e);
          localStorage.removeItem(key);
          return null;
        }
      }
      return value;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      console.log(`Supabase storage setItem(${key}):`, value ? 'setting value' : 'null value');
      
      // Temporarily disable validation on setItem too
      if (key.includes('auth-token') && value) {
        try {
          const parsed = JSON.parse(value);
          console.log(`Storing session data with keys:`, Object.keys(parsed));
        } catch (e) {
          console.warn(`Invalid JSON for session data for key ${key}`, e);
          return;
        }
      }
      localStorage.setItem(key, value);
      console.log(`Supabase storage setItem(${key}): success`);
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  },
  removeItem: (key: string): void => {
    try {
      console.log(`Supabase storage removeItem(${key})`);
      // Add stack trace to see what's calling removeItem
      if (key.includes('auth-token')) {
        console.log('Session removal stack trace:', new Error().stack);
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  }
};

// Create the Supabase client with standard configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage
  },
  global: {
    headers: {
      "X-Client-Info": "rendezvous-web",
    },
  },
});

// Add auth state change listener for debugging
supabase.auth.onAuthStateChange((event, session) => {
  console.log('Auth state changed:', event, session?.user?.id);
});
